import React from "react";
import { Dialog, DialogProps, Slide } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

// Shared shell for every mobile-tailored popup on the Transactions page
// (transaction-detail-mobile.tsx, link-transaction-mobile.tsx, and further
// ones as they're built) - a native-feeling bottom sheet instead of a
// centered/full-screen Dialog: slides up from the bottom, occupies a fixed
// share of the viewport height, rounded top corners only. Kept as its own
// component so all of them stay visually/behaviorally consistent and a
// future tweak (e.g. drag-to-dismiss) only needs to happen once.
export const SlideUpTransition = React.forwardRef(function SlideUpTransition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface BottomSheetDialogProps extends Omit<DialogProps, 'PaperProps' | 'sx'> {
    // Share of the viewport height the sheet occupies. Defaults to 85 - tall
    // enough to feel like the primary surface, short enough to keep the
    // originating page visible (and tappable-to-dismiss) behind it. Ignored
    // when fitContent is true.
    heightVh?: number;
    // For short confirm-style popups (a message + a couple buttons) a fixed
    // 85vh sheet leaves a big dead gap below the content. fitContent sizes
    // the sheet to its own content instead, only capping it at heightVh so a
    // sheet with unexpectedly long content still scrolls rather than
    // overflowing the viewport.
    fitContent?: boolean;
}

const BottomSheetDialog: React.FC<BottomSheetDialogProps> = ({ heightVh = 85, fitContent = false, children, ...dialogProps }) => (
    <Dialog
        TransitionComponent={SlideUpTransition}
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-end' } }}
        PaperProps={{
            sx: {
                m: 0,
                width: '100%',
                maxWidth: '100%',
                ...(fitContent
                    ? { maxHeight: `${heightVh}vh` }
                    : { height: `${heightVh}vh`, maxHeight: `${heightVh}vh` }),
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
            },
        }}
        {...dialogProps}
    >
        {children}
    </Dialog>
);

export default BottomSheetDialog;
